import {
  RotateCcw,
  Package2,
  Building2,
  Tags,
  Filter
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';

import type {
  ICompanyItemGroupedSupplyInterface,
  ICompanyItemInterface,
  ICompanyList,
} from '../../api/CompanyItems.interface';
import GroupItemsDialog from '../GroupItemsDialog';

import ExcelBtn from './ExcelBtn';
import ItemSelection from './ItemSelection';
import SupplierSelection from './supplierSelection';
import TypeSelection from './TypeSelection';

type Props = {
  setFilterData: React.Dispatch<
    React.SetStateAction<ICompanyItemInterface[] | null>
  >;
  filterData: ICompanyItemInterface[] | null;
  groupedData: ICompanyItemGroupedSupplyInterface[] | null;
  allData: ICompanyItemInterface[] | null;
  selectedCompany: ICompanyList | null;
  startDate: Date | null;
  endDate: Date | null;
};

function FilterBox({
  setFilterData,
  allData,
  filterData,
  groupedData,
  endDate,
  selectedCompany,
  startDate,
}: Props) {
  const [items, setItems] = useState<
    { label: string; value: number; isImportant: boolean }[]
  >([]);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);

  const [suppliers, setSuppliers] = useState<
    { label: string; value: number }[]
  >([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<number[]>([]);
  const [allTypes, setAllTypes] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);

  useEffect(() => {
    if (filterData && allData) {
      const uniqueSuppliers = Array.from(
        new Map(
          allData.map((item) => [
            item.ClientID,
            {
              value: item.ClientID,
              label: item.ClientName,
            },
          ]),
        ).values(),
      );
      setSuppliers(uniqueSuppliers);

      const allDataFiltered = allData.filter(
        (item) =>
          (selectedSuppliers.length === 0 ||
            selectedSuppliers.includes(item.ClientID)) &&
          (selectedTypes.length === 0 || selectedTypes.includes(item.type)),
      );

      const uniqueItems = Array.from(
        new Map(
          allDataFiltered.map((item) => [
            item.ItemID,
            {
              value: item.ItemID,
              label: item.ItemName,
              isImportant: item.ImportantItem,
            },
          ]),
        ).values(),
      );
      setItems(uniqueItems);

      const uniqueTypes = Array.from(
        new Set(filterData.map((item) => item.type)),
      );
      setAllTypes(uniqueTypes);
    }
  }, [allData, filterData, selectedSuppliers, selectedTypes]);

  useEffect(() => {
    setFilterData(
      allData?.filter(
        (item) =>
          (selectedItems.length === 0 || selectedItems.includes(item.ItemID)) &&
          (selectedTypes.length === 0 || selectedTypes.includes(item.type)) &&
          (selectedSuppliers.length === 0 ||
            selectedSuppliers.includes(item.ClientID)),
      ) || null,
    );
  }, [selectedItems, selectedTypes, allData, setFilterData, selectedSuppliers]);

  const totalFilters = selectedItems.length + selectedTypes.length + selectedSuppliers.length;
  
  const clearAllFilters = () => {
    setSelectedItems([]);
    setSelectedTypes([]);
    setSelectedSuppliers([]);
  };

  return (
    <div className="flex flex-col h-full space-y-4">
      {/* Filter Summary & Actions */}
      <div className="flex-shrink-0 rounded-lg border border-slate-200 bg-slate-50/50 p-6 dark:border-slate-700 dark:bg-slate-800/30">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-600 dark:text-slate-400" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              فلاتر التصفية
            </span>
            {totalFilters > 0 && (
              <Badge variant="secondary" className="h-5 px-2 text-xs">
                {totalFilters}
              </Badge>
            )}
          </div>
          {totalFilters > 0 && (
            <Button
              onClick={clearAllFilters}
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              مسح الكل
            </Button>
          )}
        </div>
        
        {/* Export Actions */}
        <div className="grid grid-cols-1 gap-2">
          {filterData && (
            <div className="flex gap-2">
              <ExcelBtn
                items={filterData}
                endDate={endDate}
                startDate={startDate}
                selectedCompany={selectedCompany}
              />
              <GroupItemsDialog
                endDate={endDate}
                startDate={startDate}
                selectedCompany={selectedCompany}
                items={groupedData}
              />
            </div>
          )}
        </div>
      </div>

      {/* Filter Sections */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full pr-2">
          <div className="space-y-4 pb-4">
          {/* Suppliers Filter */}
          <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/50">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    تصفية الموردين
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {suppliers.length} مورد
                  </p>
                </div>
                {selectedSuppliers.length > 0 && (
                  <Badge variant="soft" className="h-6 px-2 text-xs">
                    {selectedSuppliers.length}
                  </Badge>
                )}
              </div>
            </div>
            <div className="p-4">
              <SupplierSelection
                setSelectedSuppliers={setSelectedSuppliers}
                suppliers={suppliers}
                selectedSuppliers={selectedSuppliers}
              />
            </div>
          </div>

          {/* Items Filter */}
          <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/50">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                  <Package2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    تصفية الأصناف
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {items.length} صنف
                  </p>
                </div>
                {selectedItems.length > 0 && (
                  <Badge variant="soft" className="h-6 px-2 text-xs">
                    {selectedItems.length}
                  </Badge>
                )}
              </div>
            </div>
            <div className="p-4">
              <ItemSelection
                items={items}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
              />
            </div>
          </div>

          {/* Types Filter */}
          <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/50">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/30">
                  <Tags className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    تصفية الأنواع
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {allTypes.length} نوع
                  </p>
                </div>
                {selectedTypes.length > 0 && (
                  <Badge variant="soft" className="h-6 px-2 text-xs">
                    {selectedTypes.length}
                  </Badge>
                )}
              </div>
            </div>
            <div className="p-4">
              <TypeSelection
                allTypes={allTypes}
                selectedTypes={selectedTypes}
                setSelectedTypes={setSelectedTypes}
              />
            </div>
          </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}

export default FilterBox;
